/**
 * The capture port for the quick-note panel (feedback quick capture spec 01 §2): what only a platform can do when
 * someone raises feedback, which is picture the screen and remember what the console reported.
 *
 * A port of its own rather than more methods on `PlatformCapabilities`: only the staff web shell raises feedback, so
 * the customer and supplier portals are not made to implement it. The web shell implements it in
 * `apps/frontend/src/platform/feedback-capture.ts` and provides it at the app root; without a provider (a story, a
 * test) the panel still works, with no screenshot and no console errors.
 */
import { createContext, useContext, type ReactNode } from "react";

/** An element carrying this attribute is left out of the screenshot: the panel itself, and the button that opens it. */
export const FEEDBACK_IGNORE_ATTRIBUTE = "data-feedback-ignore";

export interface FeedbackCapture {
  /**
   * An image of the page as it is now, the feedback panel itself left out. Resolves a PNG, JPEG or WebP within the
   * note's size cap. Rejects when the page cannot be drawn; the panel then says so and the note is still sent.
   */
  captureScreenshot(): Promise<Blob>;
  /** The last few console errors, oldest first, each already made safe to send (one line, no query strings). */
  recentConsoleErrors(): { message: string }[];
}

/** No capture available: the panel is a plain note box. */
export const noFeedbackCapture: FeedbackCapture = {
  captureScreenshot: () =>
    Promise.reject(new Error("Screenshots are not available here.")),
  recentConsoleErrors: () => [],
};

const FeedbackCaptureContext =
  createContext<FeedbackCapture>(noFeedbackCapture);

export function FeedbackCaptureProvider({
  capture,
  children,
}: {
  capture: FeedbackCapture;
  children: ReactNode;
}) {
  return (
    <FeedbackCaptureContext.Provider value={capture}>
      {children}
    </FeedbackCaptureContext.Provider>
  );
}

export function useFeedbackCapture(): FeedbackCapture {
  return useContext(FeedbackCaptureContext);
}
